import { Directive, ElementRef, Input, AfterViewInit, OnDestroy } from '@angular/core';

/**
 * Anima el número que ya está en el texto del elemento, contando desde 0 hasta ese valor
 * cuando entra en el viewport (IntersectionObserver + requestAnimationFrame, una sola vez).
 *
 * No recibe el número por @Input() a propósito: lee el textContent existente (p. ej.
 * "+ 195") con una regex, separa el prefijo/sufijo del número, y solo anima esa parte —
 * así funciona tal cual con el binding de Angular que ya está en el template
 * ({{destiny?.title}}) sin tener que duplicar el dato ni parsear nada en el .ts del
 * componente. Si el texto no tiene ningún número, no hace nada (deja el texto como está).
 *
 * Uso:
 *   <p class="title-number" appCountUp>{{destiny?.title}}</p>
 */
@Directive({
  selector: '[appCountUp]'
})
export class CountUpDirective implements AfterViewInit, OnDestroy {
  @Input() countUpDuration = 1400;

  private rafId?: number;
  private observer?: IntersectionObserver;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    const el = this.el.nativeElement;
    const text = el.textContent ?? '';
    const match = text.match(/\d[\d.,]*/);
    if (!match || match.index === undefined) return;

    const rawNumber = match[0];
    const target = Number(rawNumber.replace(/[.,]/g, ''));
    if (!Number.isFinite(target)) return;

    const prefix = text.slice(0, match.index);
    const suffix = text.slice(match.index + rawNumber.length);
    const useThousandsSeparator = /[.,]/.test(rawNumber);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.animate(el, target, prefix, suffix, useThousandsSeparator);
        this.observer?.disconnect();
      }
    }, { threshold: 0.3 });

    this.observer.observe(el);
  }

  private animate(el: HTMLElement, target: number, prefix: string, suffix: string, useThousandsSeparator: boolean): void {
    const start = performance.now();
    const duration = this.countUpDuration;

    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      // ease-out cubic: arranca rápido y frena hacia el final, se siente menos "mecánico"
      // que una interpolación lineal para este tipo de contador.
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(target * eased);
      const formatted = useThousandsSeparator ? current.toLocaleString('es-AR') : String(current);
      el.textContent = `${prefix}${formatted}${suffix}`;

      if (progress < 1) {
        this.rafId = requestAnimationFrame(step);
      }
    };

    this.rafId = requestAnimationFrame(step);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.rafId !== undefined) {
      cancelAnimationFrame(this.rafId);
    }
  }
}
